import React, { useState, useRef, useEffect, useId } from 'react';
import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useUserClaimedProfiles } from './hooks/useUserClaimedProfiles';
import './UserAccountMenu.css';

const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;

const UserAccountMenu = ({ user, closeMobileMenu }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { claimedProfiles, loading, hasDancerProfiles, hasStudioProfiles, hasAnyProfiles } = useUserClaimedProfiles();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const panelId = useId();

  // Close when clicking/tapping outside, or pressing Escape (focus returns to the button)
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleNavigate = () => {
    setIsOpen(false);
    if (closeMobileMenu) closeMobileMenu();
  };

  if (!user) return null;

  const dancerCount = claimedProfiles.dancers.length;
  const studioCount = claimedProfiles.studios.length;
  const profileSummary = [
    dancerCount > 0 && plural(dancerCount, 'Dancer'),
    studioCount > 0 && plural(studioCount, 'Studio'),
  ].filter(Boolean).join(' • ') || 'No claimed profiles';

  const email = user.email || 'Account';
  const initial = email.trim().charAt(0).toUpperCase();

  return (
    <div className={`uam ${isOpen ? 'uam--open' : ''}`} ref={menuRef}>
      <button
        type="button"
        ref={buttonRef}
        className="uam-trigger"
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="uam-avatar" aria-hidden="true">{initial}</span>
        <span className="uam-identity">
          <span className="uam-email">{email}</span>
          <span className="uam-summary">{loading ? 'Loading…' : profileSummary}</span>
        </span>
        <svg className="uam-chevron" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
          <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div className="uam-panel" id={panelId}>
          <div className="uam-section">
            <Link to="/my-feed" className="uam-item" onClick={handleNavigate}>
              <span className="uam-item-icon" aria-hidden="true">📰</span>
              <span className="uam-item-label">My Feed</span>
            </Link>
            <Link to="/my-dashboard" className="uam-item" onClick={handleNavigate}>
              <span className="uam-item-icon" aria-hidden="true">👤</span>
              <span className="uam-item-label">My Dashboard</span>
            </Link>
          </div>

          {loading ? (
            <p className="uam-status" role="status">Loading profiles…</p>
          ) : hasAnyProfiles ? (
            <>
              {hasDancerProfiles && (
                <div className="uam-section">
                  <h3 className="uam-heading">
                    <span aria-hidden="true">💃</span> Dancer Profile{dancerCount > 1 ? 's' : ''}
                  </h3>
                  {claimedProfiles.dancers.map((dancer) => (
                    <Link
                      key={dancer.id}
                      to={`/dancer/${encodeURIComponent(dancer.name)}`}
                      className="uam-item uam-item--profile"
                      onClick={handleNavigate}
                    >
                      <span className="uam-item-label">{dancer.name}</span>
                    </Link>
                  ))}
                </div>
              )}

              {hasStudioProfiles && (
                <div className="uam-section">
                  <h3 className="uam-heading">
                    <span aria-hidden="true">🏛️</span> Studio Profile{studioCount > 1 ? 's' : ''}
                  </h3>
                  {claimedProfiles.studios.map((studio) => (
                    <Link
                      key={studio.id}
                      to={`/studio/${encodeURIComponent(studio.name)}`}
                      className="uam-item uam-item--profile"
                      onClick={handleNavigate}
                    >
                      <span className="uam-item-label">{studio.displayName || studio.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="uam-status">No claimed profiles</p>
          )}

          <div className="uam-section">
            <button type="button" className="uam-item uam-item--logout" onClick={handleLogout}>
              <span className="uam-item-icon" aria-hidden="true">🚪</span>
              <span className="uam-item-label">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccountMenu;
